import dayjs from '../utils/dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Sidebar from '../config/sidebar';
import Logo from 'media/images/logo.svg';

import {
  Text,
  Frame,
  Toast,
  Icon,
  TopBar,
  Link,
  Avatar,
  ResourceList,
  BlockStack,
  Box,
  ResourceItem,
  EmptyState,
  SkeletonBodyText,
  Form,
  TextField,
  Button,
  useBreakpoints,
  Badge,
  InlineStack,
  Divider,
} from '@shopify/polaris';
import {
  ExitIcon,
  ProfileIcon,
  PhoneInIcon,
  NotificationIcon,
  SettingsFilledIcon,
  PersonAddIcon,
  AlertBubbleIcon,
  CheckIcon,
  XSmallIcon,
} from '@shopify/polaris-icons';
import SidebarPopup from 'components/sidebarPopup';
import PhoneCallLog from './phoneCall';
import MyNotificationLog from './notificationLog';
import FlightRadar from './flightRadar';
import helpers from 'helpers/index';
import __ from 'languages/index';
import { useUserLogout } from 'queries/user.query';
import { useAuth } from 'context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import Capacity from 'components/Capacity';
import { useSocket } from 'context/SocketContext';
import { useFloatingBanner } from 'context/FloatingBannerProvider';
import { useMutateCreateCustomer, useMutateSearchCustomer } from 'queries/customer.query';
import queryClient from 'queries/index';
import { useMutateSearchUser } from 'queries/users.query';
import { ENUM_ORDER_PAYMENT_STATUS, useMutateSearchOrders } from 'queries/orders.query';

import { getOrderStatus } from 'entities/orders/order.util';
import PercentCircle from 'components/PercentCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlane } from '@fortawesome/free-solid-svg-icons';

type FlightNotification = {
  title: string;
  message: string;
  notification_title: string;
  notification_content: string;
  flight_radar_id: string;
  flight: string;
  status: 'takeoff' | 'landing';
};

type TypedMessageForCustomer = {
  phonenumber: string;
  display_name: string;
  user_nickname: string;
  customer_id: string | null;
  source: string;
  customer_order_success_count: number;
  customer_order_fail_count: number;
  customer_moneyspent_count: number;
  trush_rate: number;
  is_vip: number;
};

function usePolarisBackdropSync() {
  useEffect(() => {
    const navEl = document.getElementById('AppFrameNav');
    if (!navEl) return;

    const updateBackdropClass = () => {
      // backdrop do Polaris tự render
      const backdrop = document.querySelector('.Polaris-Backdrop.Polaris-Backdrop--belowNavigation') as HTMLElement | null;

      if (!backdrop) return;

      const isVisible = navEl.classList.contains('Polaris-Frame__Navigation--visible');

      // nếu visible => add class showNow, ngược lại remove
      backdrop.classList.toggle('Polaris-Backdrop--belowNavigationShowNow', isVisible);
    };

    // check ngay lần đầu
    updateBackdropClass();

    // quan sát thay đổi class trên nav
    const observer = new MutationObserver(() => {
      updateBackdropClass();
    });

    observer.observe(navEl, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

// rootReducer.js

export default function AppFrame({ children, hasSidebar = true }: { children: any; hasSidebar?: boolean }) {
  const history = useNavigate();

  // let { search } = useLocation();

  /**
   * Check login ...
   */

  const skipToContentRef = useRef(null);
  const { isAuthenticated, user: account } = useAuth();

  const [userMenuActive, setUserMenuActive] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);

  const toggleUserMenuActive = useCallback(() => setUserMenuActive((_userMenuActive) => !_userMenuActive), []);
  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((_mobileNavigationActive) => !_mobileNavigationActive), []);

  const { mutateAsync: logMeOut, isSuccess: isLogoutSuccess } = useUserLogout();

  useEffect(() => {
    if (isLogoutSuccess) {
      history('/login');
    }
  }, [isLogoutSuccess]);

  const logOutCallback = useCallback(async () => {
    helpers.cookie_delete('AT');
    helpers.cookie_delete('RT');
    helpers.cookie_delete('EA');
    /** xóa app id để không dính lỗi SSO */
    helpers.cookie_set('redirect', window.location.href);
    // load lại để log out ra ngoài ...
    await logMeOut();
  }, []);

  const userMenuMarkup = !helpers.isEmpty(account) ? (
    <TopBar.UserMenu
      customWidth="250px"
      actions={[
        {
          items: [
            { content: __('my_profile'), url: '/profile', icon: ProfileIcon },
            { content: __('edit_my_profile'), url: '/edit-my-profile', icon: SettingsFilledIcon },
          ],
        },
        {
          items: [{ content: __('logout'), icon: ExitIcon, onAction: () => logOutCallback() }],
        },
      ]}
      name={account?.display_name || account?.user_email || account?.user_login}
      detail={account?.user_email}
      initials={String(account?.display_name || account?.user_email || account?.user_login || 'unknown').charAt(0)}
      open={userMenuActive}
      avatar={account.user_avatar ? __helpers.getMediaLink(account.user_avatar) : null}
      onToggle={toggleUserMenuActive}
    />
  ) : (
    <TopBar.UserMenu
      actions={[
        {
          items: [
            { content: __('login'), url: '/login', icon: ExitIcon },
            { content: __('register'), url: '/register', icon: ProfileIcon },
          ],
        },
      ]}
      name={__('welcome')}
      detail={__('customer')}
      initials="U"
      open={userMenuActive}
      onToggle={toggleUserMenuActive}
    />
  );

  /** Tìm kiếm */
  const { addNotification } = useNotification();
  const [searchActive, setSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [customerName, setCustomerName] = useState('');
  const { mutateAsync: createCustomer, isPending: isCreatingCustomer, isSuccess: isCreateCustomerSuccess } = useMutateCreateCustomer();

  useEffect(() => {
    if (isCreateCustomerSuccess) {
      setCustomerName('');
      setSearchValue('');
      setSearchActive(false);
    }
  }, [isCreateCustomerSuccess]);

  const createNewCustomerCallback = useCallback(async () => {
    if (!customerName.trim()) return;
    try {
      let newCustomerData = await createCustomer({
        user_phonenumber: helpers.normalizePhoneNumber(searchValue),
        display_name: customerName,
      });
      addNotification('info', 'Khách hàng đã được tạo thành công');
      history(`/customer/view/${newCustomerData.user_id}`);
    } catch (error) {
      console.error(error);
    }
  }, [customerName, searchValue, createCustomer]);

  const {
    mutateAsync: searchCustomer,
    reset: resetSearchingCustomer,
    data: searchCustomerData,
    isPending: searchingCustomer,
  } = useMutateSearchCustomer();
  const { mutateAsync: searchUser, reset: resetSearchingUser, data: searchUserData, isPending: searchingUser } = useMutateSearchUser();
  const { mutateAsync: searchOrder, reset: resetSearchingOrder, data: orderSearchData, isPending: searchingOrder } = useMutateSearchOrders();

  const handleSearchResultsDismiss = useCallback(() => {
    setSearchActive(false);
    setSearchValue('');
  }, []);
  const handleSearchFieldChange = useCallback((value: string) => {
    setSearchValue(value);
    setSearchActive(value.length > 0);
    /** reset toàn bộ dữ liệu */
    resetSearchingCustomer();
    resetSearchingUser();
    resetSearchingOrder();
  }, []);

  const getSearchCallback = useCallback(async (searchValue: string) => {
    searchCustomer({
      // helpers.normalizePhoneNumber
      query: searchValue,
      limit: 2,
    });
    searchUser({
      query: searchValue,
      user_role: '!customer',
      limit: 2,
    });
    searchOrder({
      query: searchValue,
      limit: 2,
    });
  }, []);

  const searchDebound = useMemo(() => helpers.debounce((_value) => getSearchCallback?.call(this, _value), 500), []);

  useEffect(() => {
    if (!searchValue) {
      return;
    }
    // Do search here
    searchDebound(searchValue);
    // Khi người dùng nhập từ khóa mới -> reset tên khách hàng
    setCustomerName('');
  }, [searchValue]);

  const emptyStateMarkup = useMemo(() => {
    const isPhoneNumber = helpers.isPhoneNumber(searchValue);
    if (searchCustomerData && !searchCustomerData.length && searchUserData && !searchUserData.length) {
      if (isPhoneNumber) {
        return (
          <Box padding="400">
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Tạo khách hàng mới
              </Text>
              <Form onSubmit={createNewCustomerCallback}>
                <BlockStack gap="400">
                  <TextField label="Số điện thoại" labelHidden value={helpers.normalizePhoneNumber(searchValue)} readOnly autoComplete="off" />
                  <TextField
                    autoFocus
                    label="Tên khách hàng"
                    labelHidden
                    placeholder="Tên khách hàng"
                    value={customerName}
                    onChange={(t) => setCustomerName(helpers.capitalizeWords(t))}
                    autoComplete="off"
                  />
                  <Button submit variant="primary" icon={PersonAddIcon} disabled={!customerName.trim()} loading={isCreatingCustomer}>
                    Tạo khách hàng
                  </Button>
                </BlockStack>
              </Form>
            </BlockStack>
          </Box>
        );
      }
      return (
        <EmptyState heading="Không thấy kết quả nào" image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png">
          <p>Kiểm tra lại mã vé hoặc số điện thoại của khách.</p>
        </EmptyState>
      );
    }
    return null;
  }, [searchCustomerData, customerName, searchValue, isCreatingCustomer, searchUserData]);

  const searchResultsMarkup = (
    <Box width="100%" padding={'0'}>
      {searchingCustomer || searchingOrder || searchingUser ? (
        <Box width="100%" padding="400">
          <SkeletonBodyText />
        </Box>
      ) : null}

      {!searchingCustomer && searchCustomerData && searchCustomerData?.length < 1 && orderSearchData?.length < 1 && emptyStateMarkup}

      <BlockStack gap={'200'}>
        {searchCustomerData && searchCustomerData.length > 0 && (
          <ResourceList
            headerContent="Khách hàng"
            showHeader
            resourceName={{ singular: 'customer', plural: 'customers' }}
            items={searchCustomerData || []}
            loading={searchingCustomer}
            emptyState={emptyStateMarkup}
            renderItem={(customer) => {
              const { display_name, user_phonenumber, user_id, is_vip, customer_to_user } = customer;
              const media = <Avatar size="md" name={display_name} initials={is_vip ? 'VIP' : display_name.charAt(0)} />;
              const url = `/customer/view/${user_id}`;
              return (
                <ResourceItem
                  id={user_id}
                  onClick={() => {
                    setSearchActive(false);
                    if (url) window.location.replace(url);
                  }}
                  media={media}
                  accessibilityLabel={`View details for ${display_name}`}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {display_name}
                  </Text>
                  <div>
                    {user_phonenumber} - {customer_to_user.customer_order_success_count + ' đơn'}
                  </div>
                </ResourceItem>
              );
            }}
          />
        )}
        {searchUserData && searchUserData.length > 0 && (
          <ResourceList
            headerContent="Nhân viên"
            showHeader
            resourceName={{ singular: 'customer', plural: 'customers' }}
            items={searchUserData || []}
            loading={searchingUser}
            emptyState={emptyStateMarkup}
            renderItem={(userData) => {
              const { display_name, user_phonenumber, user_id } = userData;
              const media = <Avatar size="md" name={display_name} initials={display_name.charAt(0)} />;
              const url = `/users/view/${user_id}`;
              return (
                <ResourceItem
                  id={user_id}
                  onClick={() => {
                    setSearchActive(false);
                    if (url) window.location.replace(url);
                  }}
                  media={media}
                  accessibilityLabel={`View details for ${display_name}`}
                >
                  <Text variant="bodyMd" fontWeight="bold" as="h3">
                    {display_name}
                  </Text>
                  <div>{user_phonenumber}</div>
                </ResourceItem>
              );
            }}
          />
        )}
        {orderSearchData?.length > 0 && (
          <ResourceList
            headerContent={`Tìm thấy ${orderSearchData?.length || 0} đơn hàng`}
            showHeader
            resourceName={{ singular: 'Đơn hàng', plural: 'Đơn hàng' }}
            items={orderSearchData || []}
            loading={searchingOrder}
            renderItem={(order) => {
              const {
                order_code,
                id,
                payment_status,
                order_status,
                total_must_pay,
                journey_id,
                journey,
                customer_data,
                total_seat,
                departure_date,
                invoices,
                ticket,
              } = order;

              return (
                <ResourceItem
                  id={id}
                  onClick={() => {
                    setSearchActive(false);
                    window.location.replace(`/orders/view/${id}`);
                  }}
                  media={null}
                  accessibilityLabel={`View details for ${id}`}
                >
                  <InlineStack align="start" blockAlign="center" gap="400">
                    <Text variant="bodyMd" fontWeight="bold" as="h3">
                      {order_code}
                    </Text>
                    {payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID ? (
                      <Badge tone="success" icon={CheckIcon}>
                        PAID
                      </Badge>
                    ) : (
                      <Badge tone="attention" icon={XSmallIcon}>
                        UNPAY
                      </Badge>
                    )}

                    {invoices ? (
                      <Badge tone="success" icon={CheckIcon}>
                        INVOICE
                      </Badge>
                    ) : (
                      <Badge tone="attention" icon={XSmallIcon}>
                        INVOICE
                      </Badge>
                    )}

                    {getOrderStatus(order_status)}
                  </InlineStack>
                  <div>
                    <Text as="p" fontWeight="bold">
                      {customer_data?.display_name} / {customer_data?.user_phonenumber}
                    </Text>
                    <Text as="p">
                      {total_seat} chỗ - {__helpers.formatNumber(total_must_pay) + ' VND'}
                    </Text>
                    <Text as="p">
                      {dayjs(new Date(Number(departure_date))).format('HH:mm DD/MM/YYYY')} - {journey?.journey_from} đi {journey?.journey_to}
                    </Text>
                  </div>
                </ResourceItem>
              );
            }}
          />
        )}
      </BlockStack>
    </Box>
  );

  const searchFieldMarkup = (
    <TopBar.SearchField onChange={handleSearchFieldChange} value={searchValue} placeholder="Tìm theo mã vé hoặc số điện thoại" />
  );

  /**
   * Nút show bẹn website
   */
  const [showPhoneCallLog, setShowPhoneCallLog] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showFlightRadar, setShowFlightRadar] = useState(false);

  const navigate = useNavigate();

  const phonelogMarkup = (
    <TopBar.Menu
      key="OpenPhoneBook"
      activatorContent={
        <span>
          <Icon source={PhoneInIcon} />
        </span>
      }
      open={false}
      actions={[]}
      // onOpen={() => setShowPhoneCallLog(true)}
      onOpen={() => {
        /** tạm thời chuyển về đây theo ý khách hàng */
        navigate('/phonelog');
      }}
      onClose={() => {}}
    />
  );

  const notificationSidebarMenuActivator = (
    <TopBar.Menu
      key="OpenNotification"
      activatorContent={
        <span>
          <Icon source={NotificationIcon} />
        </span>
      }
      open={false}
      actions={[]}
      onOpen={() => setShowNotification(true)}
      onClose={() => alert('close notification')}
    />
  );

  const flightRadarSidebarMenuActivator = (
    <TopBar.Menu
      key="OpenFlightRadar"
      accessibilityLabel="Theo dõi máy bay"
      activatorContent={
        <span title="Theo dõi máy bay">
          <FontAwesomeIcon icon={faPlane} style={{ width: 20, height: 20 }} />
        </span>
      }
      open={false}
      actions={[]}
      onOpen={() => setShowFlightRadar(true)}
      onClose={() => setShowFlightRadar(false)}
    />
  );

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      userMenu={userMenuMarkup}
      searchResultsVisible={searchActive}
      searchField={isAuthenticated && searchFieldMarkup}
      searchResults={searchResultsMarkup}
      onSearchResultsDismiss={handleSearchResultsDismiss}
      onNavigationToggle={toggleMobileNavigationActive}
      secondaryMenu={isAuthenticated ? [phonelogMarkup, notificationSidebarMenuActivator, flightRadarSidebarMenuActivator] : []}
    />
  );

  const logo = {
    width: 150,
    topBarSource: Logo,
    url: '/',
    accessibilityLabel: 'nBee',
  };

  /** Global notification */
  const { notification, clearNotification } = useNotification();

  /** SOCKET  */
  const { socket } = useSocket(); // Lấy socket từ context
  const [messages, setMessages] = useState<TypedMessageForCustomer>(null);
  const { showBanner } = useFloatingBanner();

  useEffect(() => {
    const handleFlightNotification = (notification: FlightNotification) => {
      const title =
        notification.title ||
        notification.notification_title ||
        `Chuyến bay ${notification.flight} đã ${notification.status === 'takeoff' ? 'cất cánh' : 'hạ cánh'}`;

      queryClient.invalidateQueries({ queryKey: ['flight_radar/fetch_entity_list'] });
      queryClient.invalidateQueries({ queryKey: ['flight_radar/fetch_entity', notification.flight_radar_id] });
      showBanner({
        title,
        content: (
          <BlockStack gap="200">
            <Text as="p">{notification.message || notification.notification_content || title}</Text>
            <Button variant="plain" onClick={() => setShowFlightRadar(true)}>
              Xem theo dõi máy bay
            </Button>
          </BlockStack>
        ),
        duration: 15000,
      });
    };

    if (socket) {
      socket.on('flight_notification', handleFlightNotification);
      // Lắng nghe sự kiện từ server
      socket.on('calling', (msg: TypedMessageForCustomer) => {
        setMessages(msg);
        /** lấy dữ liệu mới cho trang gọi điện */
        queryClient.invalidateQueries({ queryKey: ['phonelog/fetch_entities_list'] });
        queryClient.invalidateQueries({ queryKey: ['phonelog/fetch_entity_list'] });
      });

      socket.on('change_trip_manifest', ({ trip_manifest_id = null, ticket_id = null, order_id = null }) => {
        queryClient.invalidateQueries({ queryKey: ['orders/fetch_entity_list'] });
        // let params = helpers.ExtractUrl(window.location.search);
        queryClient.invalidateQueries({ queryKey: ['trip/fetch_entity_list'] });
        // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order_in_manifest'] });
        // queryClient.invalidateQueries({ queryKey: ['orders/fetch_list_order_log_in_manifest'] });
        queryClient.invalidateQueries({ queryKey: ['trip_manifest/fetch_entity', trip_manifest_id] });
        // queryClient.invalidateQueries({ queryKey: ['trip_manifest_fee/fetch_entity_list'] });
        queryClient.invalidateQueries({ queryKey: ['ticket/fetch_unassigned_list'] }); // reset lại cái danh sách vé chưa điều chuyến
        // queryClient.invalidateQueries({ queryKey: ['ticket/fetch_entity_list'] }); // reset lại cái danh sách vé để hiển thị
      });

      socket.on('receive_transaction', ({ amount = 0, payment_method = 'CASH', order_id = '', description = '' }) => {
        queryClient.invalidateQueries({ queryKey: ['transactions/fetch_entity_list'] }); // reset lại cái danh sách giao dịch

        showBanner({
          title: `+${helpers.formatNumber(amount)} VND`,
          content: (
            <BlockStack gap={'100'}>
              <Text as="p">
                Giao dịch +{helpers.formatNumber(amount)} ({payment_method}) - ID đơn: {order_id || 'Không có'}
              </Text>
              <Text as="p" tone="subdued" variant="bodyXs">
                {description || '-'}
              </Text>
            </BlockStack>
          ),
          duration: 15000,
          audioType: 'tingting',
        });
      });
    }

    return () => {
      if (socket) {
        socket.off('flight_notification', handleFlightNotification);
        socket.off('calling'); // Hủy lắng nghe khi unmount
        socket.off('change_trip_manifest'); // Hủy lắng nghe khi unmount
        socket.off('receive_transaction'); // Hủy lắng nghe khi unmount
      }
    };
  }, [socket, showBanner]);

  // const sendMessage = () => {
  //   if (socket && message.trim()) {
  //     socket.emit("messageFromClient", message); // Gửi tin nhắn tới server
  //     setMessage(""); // Reset input
  //   }
  // };
  useEffect(() => {
    if (!messages) return;
    showBanner({
      title: (messages?.is_vip === 1 ? '(VIP) ' : '') + messages?.display_name + ' đang gọi',
      content: (
        <BlockStack gap={'200'}>
          <InlineStack gap="400" align="start" blockAlign="center">
            <div>
              <PercentCircle percent={messages?.trush_rate} size={60} strokeWidth={6} label="Tin cậy" />
            </div>
            <div>
              <Text as="p" variant="headingMd" numeric>
                {messages?.customer_id ? <Link url={`/customer/view/${messages?.customer_id}`}>{messages?.phonenumber}</Link> : messages?.phonenumber}{' '}
                {`(${messages?.source || 'hotline'})`}
              </Text>
              {messages.user_nickname ? (
                <Text as="p">
                  Biệt danh: <strong>{messages.user_nickname || '--'}</strong>
                </Text>
              ) : null}
            </div>
          </InlineStack>

          <Divider />

          <InlineStack align="space-between" blockAlign="center" gap="400">
            <BlockStack gap="100">
              <Text as="p">Đơn</Text>
              <Text as="p" variant="headingLg">
                {helpers.formatNumber(messages?.customer_order_success_count)}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text as="p">Hủy</Text>
              <Text as="p" variant="headingLg">
                {helpers.formatNumber(messages?.customer_order_fail_count)}
              </Text>
            </BlockStack>
            <BlockStack gap="100">
              <Text as="p">Chi tiêu</Text>
              <Text as="p" variant="headingLg">
                {helpers.formatNumber(messages?.customer_moneyspent_count)}
              </Text>
            </BlockStack>
          </InlineStack>

          <Divider />
          <BlockStack>
            {messages.customer_id ? (
              <Button tone="success" variant="plain" icon={AlertBubbleIcon} url={`/customer/view/${messages.customer_id}`}>
                Xem thông tin khách
              </Button>
            ) : (
              <Button tone="success" variant="plain" icon={AlertBubbleIcon}>
                Chưa có trong danh bạ.
              </Button>
            )}
          </BlockStack>
        </BlockStack>
      ),
      duration: 15000,
    });
  }, [messages, showBanner]);

  /** SOCKET  */

  /** Thông báo lần đầu khi vào app */
  useEffect(() => {
    setTimeout(() => {
      showBanner({
        title: 'Thông báo',
        content: <p>Một số thông báo quan trọng sẽ có mặt tại đây. Có những thông báo như cuộc gọi tới, có vé mới chờ xử lý ...</p>,
        duration: 8000,
      });
    }, 1000);
  }, []);

  const { mdUp } = useBreakpoints();

  return (
    <>
      <Frame
        topBar={topBarMarkup}
        logo={logo}
        navigation={hasSidebar && isAuthenticated && <Sidebar />}
        showMobileNavigation={mobileNavigationActive}
        onNavigationDismiss={toggleMobileNavigationActive}
        skipToContentTarget={skipToContentRef.current}
      >
        {usePolarisBackdropSync()}
        {mdUp ? <div className="Polaris-Backdrop Polaris-Backdrop--belowNavigation" onClick={() => setMobileNavigationActive(false)}></div> : null}

        {notification && (
          <Toast
            content={notification.message}
            duration={5000}
            onDismiss={() => clearNotification(notification.id)}
            error={notification.type === 'error'}
          />
        )}
        <>
          <Capacity current_user_can={['manage_phonelog', 'view_phonelog']}>
            <SidebarPopup title="Cuộc gọi" show={showPhoneCallLog} onClose={() => setShowPhoneCallLog(false)}>
              <PhoneCallLog show={showPhoneCallLog} />
            </SidebarPopup>
          </Capacity>
          {isAuthenticated && (
            <>
              <SidebarPopup title="Theo dõi máy bay" show={showFlightRadar} onClose={() => setShowFlightRadar(false)}>
                {showFlightRadar && <FlightRadar />}
              </SidebarPopup>
              <Capacity current_user_can={['manage_notifications']}>
                <SidebarPopup title="Thông báo" show={showNotification} onClose={() => setShowNotification(false)}>
                  <MyNotificationLog />
                </SidebarPopup>
              </Capacity>
            </>
          )}
        </>

        {children}
      </Frame>
    </>
  );
}

